import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { APP_NAV, useAppNavigation } from '../../context/AppNavigationContext';
import { useAuth } from '../../context/AuthContext';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_INSET,
  Sidebar,
} from './Sidebar';
import { cn } from '../../lib/utils';

const SIDEBAR_COLLAPSED_KEY = 'vi_sidebar_collapsed';

function readCollapsedPreference() {
  try {
    return sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPreference);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeNav } = useAppNavigation();
  const isChatPage = activeNav === APP_NAV.CHAT;

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      // ignore storage failures
    }
  }, [sidebarCollapsed]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const desktopSidebarWidth = sidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;
  const contentOffset = SIDEBAR_INSET + desktopSidebarWidth + SIDEBAR_INSET;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#EEF1F5] text-slate-900">
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
      />

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-slate-900/30 lg:hidden"
          aria-label="Close navigation menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        className="flex min-h-screen min-w-0 w-full flex-col transition-[padding] duration-200 ease-out max-lg:!pl-0"
        style={{ paddingLeft: contentOffset }}
      >
        <header className="sticky top-0 z-20 flex h-14 min-w-0 items-center justify-between bg-transparent px-4">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 lg:hidden"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div className="ml-auto flex min-w-0 items-center">
            {user?.displayName && (
              <span className="font-display max-w-[52vw] truncate text-sm font-semibold text-slate-600 sm:max-w-none">
                Hello {user.displayName}
              </span>
            )}
          </div>
        </header>

        <main
          className={cn(
            'flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col px-4 pt-6 sm:px-5 lg:px-6 lg:pt-8',
            isChatPage ? 'pb-4' : 'pb-5'
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
